import React, { useState, useCallback, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'

import ZydusLogo from '../../assets/images/zydus_logo.webp'
import LifeKaFilterLogo from '../../assets/images/life_ka_filter_logo.webp'
import { ReactComponent as MenuIcon } from '../../assets/icons/menu.svg'
import { ReactComponent as CloseIcon } from '../../assets/icons/close.svg'

const ICON_WHITE_FILTER = 'brightness(0) invert(1)'

const NAV_ITEMS = [
  { label: 'Home', path: '/' },
  { label: 'Books', path: '/ckdbook?from=CKD' },
  { label: 'Awareness Films', path: '/awareness-films' },
  { label: 'All Podcasts', path: '/podcasts' },
  { label: 'About CKD', path: '/about' },
]

function navItemMatches(item, { pathname, search }) {
  const [itemPath, queryPart] = item.path.split('?')
  if (pathname !== itemPath) return false
  if (!queryPart) return true
  const itemParams = new URLSearchParams(queryPart)
  const locParams = new URLSearchParams(
    search.startsWith('?') ? search.slice(1) : search,
  )
  for (const [key, value] of itemParams.entries()) {
    if (locParams.get(key) !== value) return false
  }
  return true
}

const ZydusHeader = () => {
  const [opened, setOpened] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const activeNavIndex = NAV_ITEMS.findIndex((item) =>
    navItemMatches(item, location),
  )

  useEffect(() => {
    document.body.style.overflow = opened ? 'hidden' : ''
    return () => {
      document.body.style.overflow = ''
    }
  }, [opened])

  const handleNavClick = useCallback(
    (item) => {
      setOpened(false)
      navigate(item.path)
    },
    [navigate],
  )

  const goHome = useCallback(() => navigate('/'), [navigate])

  return (
    <>
      {/* ── MOBILE HEADER BAR ── */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="bg-white rounded-full px-3 shadow-[0_2px_4px_0_rgba(0,0,0,0.5)] border border-gray-200">
            <button
              onClick={goHome}
              className="shrink-0 bg-white rounded-full py-1"
              aria-label="Go to Home"
            >
              <img src={ZydusLogo} alt="Zydus" className="h-9 w-auto" />
            </button>
            <button
              onClick={goHome}
              className="shrink-0 bg-white rounded-full py-1 ml-2"
              aria-label="Go to Home"
            >
              <img
                src={LifeKaFilterLogo}
                alt="Life Ka Filter"
                className="h-9 w-auto"
              />
            </button>
          </div>
          <button
            onClick={() => setOpened((prev) => !prev)}
            className="w-10 h-10 rounded-full bg-background-brand-teal flex items-center justify-center shrink-0"
            aria-label={opened ? 'Close menu' : 'Open menu'}
          >
            {opened ? (
              <CloseIcon
                className="h-4 w-4"
                style={{ filter: ICON_WHITE_FILTER }}
              />
            ) : (
              <MenuIcon
                className="h-5 w-5"
                style={{ filter: ICON_WHITE_FILTER }}
              />
            )}
          </button>
        </div>
      </div>

      {/* ── MOBILE MENU OVERLAY ── */}
      <div
        className={`lg:hidden fixed inset-0 z-40 bg-white flex flex-col transition-all duration-300 ease-in-out ${
          opened
            ? 'opacity-100 translate-y-0 pointer-events-auto'
            : 'opacity-0 -translate-y-4 pointer-events-none'
        }`}
        style={{ paddingTop: 66 }}
      >
        <nav className="flex flex-col overflow-y-auto">
          {NAV_ITEMS.map((item, index) => (
            <button
              key={item.label}
              className={`px-5 py-4 text-left border-b border-border-tertiary font-manrope font-medium text-lg transition-colors ${
                activeNavIndex === index
                  ? 'text-background-brand-teal bg-background-brand-secondary-teal'
                  : 'text-foreground-primary active:bg-gray-50'
              }`}
              onClick={() => handleNavClick(item)}
            >
              {item.label}
            </button>
          ))}
        </nav>
      </div>

      {/* ── DESKTOP HEADER ── */}
      <div className="hidden lg:block fixed top-[26px] left-0 right-0 z-50 px-6">
        <div className="max-w-[1088px] mx-auto bg-white rounded-full px-6 py-2.5 flex items-center justify-between shadow-[0_2px_4px_0_rgba(0,0,0,0.5)] border border-gray-200">
          <button onClick={goHome} className="shrink-0" aria-label="Go to Home">
            <img src={ZydusLogo} alt="Zydus" className="h-12 w-auto" />
          </button>

          <nav className="flex items-center gap-7">
            {NAV_ITEMS.map((item, index) => (
              <button
                key={item.label}
                onClick={() => handleNavClick(item)}
                className={`font-manrope font-medium text-sm transition-colors ${
                  activeNavIndex === index
                    ? 'text-background-brand-teal'
                    : 'text-foreground-secondary hover:text-background-brand-teal'
                }`}
              >
                {item.label}
              </button>
            ))}
          </nav>

          <button onClick={goHome} className="shrink-0" aria-label="Go to Home">
            <img
              src={LifeKaFilterLogo}
              alt="Life Ka Filter"
              className="h-12 w-auto"
            />
          </button>
        </div>
      </div>
    </>
  )
}

export default ZydusHeader
